import { useState, Fragment } from 'react'
import { Header, InlineHeader, InlineSection, Pagination, useIsReloading, Content } from '@/components/DashboardComponents'
import { Button, Spacing, HalfSpacing, ThreeQuarterSpacing, RoundIconButton, Dropdown, DropdownOption, RadioGroup, RadioOption, Checkbox } from '@/form/FormComponents'
import { Outlet, useLoaderData, useNavigate, useRouteLoaderData, useLocation } from 'react-router'
import { GenericEmptyListPlaceholder } from '@/DashboardLayout'
import { Icon } from '@/components/Icon'
import { Modal, ModalContent, ModalButtons, useModal, useErrorModal } from '@/components/Modal'
import { useMenu } from '@/components/Menu'
import { FormProvider, FormSubmitProvider } from '@/form/FormProvider'
import { Textbox } from '@/form/FormComponents'
import { useUser, useIsSuperAdmin } from '@/systems/Authentication'
import { publicAction } from '@/data/loaders'
import { ShareBox } from '@/form/ShareBox'
import { useArchiveCourseModal } from '@/pages/Courses'
import { useIsMobile, useBreakpoint } from '@/hooks/useIsMobile'

export function Organization() {
  const { userdata } = useUser();
  const manager_organizations = userdata.organizations.filter(o => o.roles.includes("admin"));

  const organization = useLoaderData().data.value;

  let tabs = [{to: `/organizations/${organization._id}`, name: "Departments and courses", end: true}];
  tabs.push({to: `/organizations/${organization._id}/people`, name: "People"});
  tabs.push({to: `/organizations/${organization._id}/settings`, name: "Settings"});

  return (
    <>
      <Header title={organization.name} backButton={(organization.parent && (organization.ancestors || []).some(a => manager_organizations.find(b => b.data._id == a))) ? (organization.parent ? "/organizations/"+organization.parent : "/organizations") : ((!organization.parent && manager_organizations.length > 1) ? "/organizations" : false)}  tabs={tabs}/>
      <Outlet/>
    </>
  )
}

export function OrganizationDepartmentsAndCourses() {
  const { organizations, courses } = useLoaderData().data.value;
  const organization = useRouteLoaderData("organization").data.value;

  if (organizations) {
    return <SubOrganizations/>
  } else {
    return <SubCourses/>
  }
}

function SubOrganizations() {
  const { organizations: sub_organizations } = useLoaderData().data.value;
  const organization = useRouteLoaderData("organization").data.value;

  const [openNewDepartmentModal] = useNewDepartmentModal();

  function renderChildren(organization) {
    const organization_id = organization._id;

    let children = sub_organizations.filter((sub_organization) => sub_organization.parent == organization_id);

    // if (children.length == 0) {
    //   return null;
    // }

    return children.sort((a, b) => a.name.localeCompare(b.name)).map(function(org) {
      let child_items = renderChildren(org);
    
      return (
        <Fragment key={org._id}>
          <OrganizationItem organization={org} parent_organization={organization} parent_children_count={children.length}/>
          {org._course_count > 0 || org._membership_count > 0 || child_items.length > 0 ? (
            <ChildGroup>
              <div className="scenario_list" columns="1">
                {org._course_count > 0 || org._membership_count > 0 ? <CourseCountItem organization={org}/> : null}
                {child_items}
              </div>
            </ChildGroup>
          ) : null}
        </Fragment>
      )
    })
  }

  return (
    <Content>
      <InlineHeader>
        <Button icon="plus" text="New department" onClick={() => {openNewDepartmentModal({parent_id: organization._id, parent_name: organization.name, course_count: 0, membership_count: 0})}}/>
      </InlineHeader>
      <InlineSection>
        {
          sub_organizations.length > 0 ? (
            <div className="scenario_list" columns="1">
              {renderChildren(organization)}
            </div>
          ) : <GenericEmptyListPlaceholder text="No departments"/>
        }
      </InlineSection>
    </Content>
  )
}

function useMoveDepartmentModal() {
  const navigate = useNavigate();
  const { organizations: sub_organizations } = useRouteLoaderData("suborganizations").data.value;
  const organization = useRouteLoaderData("organization").data.value;

  const [openErrorModal] = useErrorModal();

  const [openMoveDepartmentModal] = useModal(({ context, closeModal }) => {
    let move_organization_id = context.organization_id;

    function renderChildren(organization_id, disabled) {
      let children = sub_organizations.filter((sub_organization) => sub_organization.parent == organization_id);
  
      return children.sort((a, b) => a.name.localeCompare(b.name)).map(function(org) {
        let child_items = renderChildren(org._id, disabled || org._id == move_organization_id);
      
        return (
          <Fragment key={org._id}>
            {/* <div className="scenario_list" columns="0"> */}
              <OrganizationMoveItem organization={org} disabled={disabled || org._id == move_organization_id} onClick={() => {closeModal();handleChooseDestination({organization_id: move_organization_id, parent_id: org._id, course_count: org._course_count, membership_count: org._membership_count})}}/>
            {/* </div> */}
            {child_items.length > 0 ? <ChildGroup>
              <div className="scenario_list" columns="1">
                {child_items}
              </div>
            </ChildGroup> : null}
          </Fragment>
        )
      })
    }

    return (
      <Modal title="Move department">
        <ModalContent>
          Choose which department you want to move {sub_organizations.find(a => a._id == move_organization_id).name} inside of:
          <Spacing/>
          <div className="scenario_list" columns="1">
            <OrganizationMoveItem organization={organization} onClick={() => {closeModal();handleChooseDestination({organization_id: move_organization_id, parent_id: organization._id, course_count: organization._course_count, membership_count: organization._membership_count})}}/>
            <ChildGroup>
              <div className="scenario_list" columns="1">
                {renderChildren(organization._id)}
              </div>
            </ChildGroup>
          </div>
        </ModalContent>
        <ModalButtons>
          <Button text="Cancel" onClick={closeModal}/>
        </ModalButtons>
      </Modal>
    )
  })

  const handleChooseDestination = ({ organization_id, parent_id, course_count, membership_count }) => {
    if (course_count > 0 || membership_count > 0) {
      openConfirmMoveDepartmentDestinationConflictModal({ organization_id, parent_id, course_count, membership_count });
    } else {
      openConfirmMoveDepartmentModal({ organization_id, parent_id });
    }
  }

  const [openConfirmMoveDepartmentDestinationConflictModal] = useModal(({ context, closeModal }) => {
    const { organization_id, parent_id, course_count, membership_count } = context;

    function confirmMove() {
      closeModal();
      openConfirmMoveDepartmentModal({ organization_id, parent_id })
    }

    return (
      <Modal title="Move department">
        <ModalContent>
          The destination department you chose already has {course_count} course{course_count != 1 ? "s" : ""} and {membership_count} educator{membership_count != 1 ? "s" : ""} inside of it. 
          <Spacing/>
          A department can hold either sub-departments or courses, but not both.
          <Spacing/>
          If you continue, you'll end up with two department under the desination: the department you're moving, and a new department we'll create to hold the existing courses and educators.
          <Spacing/>
          If you don't want that, please archive or move the existing courses and educators at the destination department first.
        </ModalContent>
        <ModalButtons>
          <Button text="Cancel" className="hollow" onClick={closeModal}/>
          <Button text="Continue" className="primary" onClick={confirmMove}/>
        </ModalButtons>
      </Modal>
    )
  })

  const [openConfirmMoveDepartmentModal] = useModal(({ context, closeModal, setLoading }) => {
    let organization_id = context.organization_id;
    let parent_id = context.parent_id;

    function confirmMove() {
      setLoading(true);
      
      publicAction("moveOrganization", {
        id: organization_id,
        parent_id: parent_id,
      }).then(function() {

        closeModal();
        navigate(".", { replace: true });

      }).catch(function(error) {
        console.error(error);
        closeModal();
        openErrorModal();
      })
    }

    return (
      <Modal title="Move department">
        <ModalContent>
          {`Are you sure you want to move ${sub_organizations.find(a => a._id == organization_id).name} inside of ${organization._id == parent_id ? organization.name : sub_organizations.find(a => a._id == parent_id).name}?`}
        </ModalContent>
        <ModalButtons>
          <Button text="Cancel" className="hollow" onClick={closeModal}/>
          <Button text="Confirm" className="primary" onClick={confirmMove}/>
        </ModalButtons>
      </Modal>
    )
  })

  return [openMoveDepartmentModal];
}

function useMoveCoursesModal() {
  const navigate = useNavigate();
  const { organizations: sub_organizations } = useRouteLoaderData("suborganizations").data.value;
  const organization = useRouteLoaderData("organization").data.value;

  const [openErrorModal] = useErrorModal();

  const [openMoveCoursesModal] = useModal(({ context, closeModal }) => {
    let { organization_id: move_organization_id, course_count, membership_count, callback } = context;

    function renderChildren(organization_id) {
      let children = sub_organizations.filter((sub_organization) => sub_organization.parent == organization_id);
  
      return children.sort((a, b) => a.name.localeCompare(b.name)).map(function(org) {
        let child_items = renderChildren(org._id);
      
        return (
          <Fragment key={org._id}>
            <OrganizationMoveItem organization={org} disabled={child_items.length > 0} onClick={() => {closeModal();openConfirmMoveCoursesModal({organization_id: move_organization_id, parent_id: org._id, course_count, membership_count, callback})}}/>
            {child_items.length > 0 ? (
              <ChildGroup>
                <div className="scenario_list" columns="1">
                  {child_items}
                </div>
              </ChildGroup>
            ) : null}
          </Fragment>
        )
      })
    }

    return (
      <Modal title="Move courses and educators">
        <ModalContent>
          Choose which department you want to move {course_count} course{course_count != 1 ? "s" : ""} and {membership_count} educator{membership_count != 1 ? "s" : ""} to:
          <Spacing/>
          <div className="scenario_list" columns="1">
            <OrganizationMoveItem organization={organization} disabled={true} onClick={() => {closeModal();openConfirmMoveCoursesModal({organization_id: move_organization_id, parent_id: organization._id, course_count, membership_count, callback})}}/>
            <ChildGroup>
              <div className="scenario_list" columns="1">
                {renderChildren(organization._id)}
              </div>
            </ChildGroup>
          </div>
        </ModalContent>
        <ModalButtons>
          <Button text="Cancel" onClick={closeModal}/>
        </ModalButtons>
      </Modal>
    )
  })

  const [openConfirmMoveCoursesModal] = useModal(({ context, closeModal, setLoading }) => {
    const { organization_id, parent_id, course_count, membership_count, callback } = context;

    function confirmMove() {
      setLoading(true);
      
      publicAction("moveCoursesAndEducators", {
        id: organization_id,
        parent_id: parent_id,
      }).then(function() {

        closeModal();
        navigate(".", { replace: true });
        if (callback) {
          callback();
        }

      }).catch(function(error) {
        console.error(error);
        closeModal();
        openErrorModal();
      })
    }

    return (
      <Modal title="Move courses and educators">
        <ModalContent>
          {`Are you sure you want to move ${course_count} course${course_count != 1 ? "s" : ""} and ${membership_count} educator${membership_count != 1 ? "s" : ""} to ${sub_organizations.find(a => a._id == parent_id).name}?`}
        </ModalContent>
        <ModalButtons>
          <Button text="Cancel" className="hollow" onClick={closeModal}/>
          <Button text="Confirm" className="primary" onClick={confirmMove}/>
        </ModalButtons>
      </Modal>
    )
  })

  return [openMoveCoursesModal];
}

function useNewDepartmentModal() {
  const navigate = useNavigate();

  const [openNewDepartmentModal] = useModal(({ context, closeModal, setLoading }) => {
    let { parent_id, parent_name, course_count, membership_count } = context;

    function submitForm({ formatted_data, focusError }) {
      setLoading(true);
      
      publicAction("createOrganization", {
        parent_id,
        data: {
          name: formatted_data.name,
          shortname: formatted_data.name,
        }
      }).then(function() {

        closeModal();
        navigate(".", { replace: true });

      }).catch(function(error) {
        console.error(error);
        setLoading(false);
        focusError({name: "name", error: "Something went wrong."});
      })
    }

    return (
      <Modal title="New department">
        <FormProvider onSubmit={submitForm}>
          <FormSubmitProvider>
            {({handleSubmit}) => (
              <>
                <ModalContent>
                  <Textbox name="name" title="Department name" placeholder="Department name" required={true} />
                </ModalContent>
                <ModalButtons>
                  <Button text="Cancel" className="hollow" onClick={closeModal}/>
                  <Button text="Submit" className="primary" onClick={handleSubmit}/>
                </ModalButtons>
              </>
            )}
          </FormSubmitProvider>
        </FormProvider>
      </Modal>
    )
  })

  const [openNewDepartmentVerifyModal] = useModal(({ context, closeModal }) => {
    let { parent_id, parent_name, course_count, membership_count } = context;

    return (
      <Modal title="Courses and educators will be moved">
        <ModalContent>
          {parent_name} already contains <nobr>{course_count} course{course_count != 1 ? "s" : ""}</nobr> and <nobr>{membership_count} educator{membership_count != 1 ? "s" : ""}</nobr>.
          <Spacing/>
          A department can hold either sub-departments or courses, but not both.
          <Spacing/>
          Creating a new department here will automatically move these courses and educators into it.
          <Spacing/>
          If you don't want that, please archive or move them elsewhere first.
        </ModalContent>
        <ModalButtons>
          <Button text="Cancel" className="hollow" onClick={closeModal}/>
          <Button text="Continue" className="primary" onClick={() => {
            closeModal();
            openNewDepartmentModal({ parent_id, parent_name, course_count, membership_count });
          }}/>
        </ModalButtons>
      </Modal>
    )
  })

  const handleOpenNewDepartmentModal = ({ parent_id, parent_name, course_count, membership_count }) => {
    if (course_count > 0 || membership_count > 0) {
      openNewDepartmentVerifyModal({ parent_id, parent_name, course_count, membership_count });
    } else {
      openNewDepartmentModal({ parent_id, parent_name, course_count, membership_count });
    }
  }

  return [handleOpenNewDepartmentModal];
}

function OrganizationMoveItem({ organization, disabled, onClick }) {
  return (
    <div className="scenario_item" onClick={onClick} tabIndex={disabled ? -1 : 0} style={{opacity: disabled ? 0.5 : 1, pointerEvents: disabled ? "none" : "auto"}}>
      <div className="item_icon">
        <Icon name="domain"/>
      </div>
      <div className="item_title">{organization.name}</div>
    </div>
  )
}

function OrganizationItem({organization, parent_organization, parent_children_count}) {
  const navigate = useNavigate();

  const [openErrorModal] = useErrorModal();
  const [openNewDepartmentModal] = useNewDepartmentModal();

  const [openRenameDepartmentModal] = useModal(({ closeModal, setLoading }) => {
    function submitForm({ formatted_data, focusError }) {
      setLoading(true);
      
      publicAction("renameOrganization", {
        id: organization._id,
        data: {
          name: formatted_data.name,
          shortname: formatted_data.name,
        }
      }).then(function() {

        closeModal();
        navigate(".", { replace: true });

      }).catch(function(error) {
        console.error(error);
        setLoading(false);
        focusError({name: "name", error: "Something went wrong."});
      })
    }

    return (
      <Modal title="Rename department">
        <FormProvider onSubmit={submitForm}>
          <FormSubmitProvider>
            {({handleSubmit}) => (
              <>
                <ModalContent>
                  <Textbox name="name" placeholder="Department name" required={true} onEnter={handleSubmit} default_value={organization.name} />
                </ModalContent>
                <ModalButtons>
                  <Button text="Cancel" className="hollow" onClick={closeModal}/>
                  <Button text="Submit" className="primary" onClick={handleSubmit}/>
                </ModalButtons>
              </>
            )}
          </FormSubmitProvider>
        </FormProvider>
      </Modal>
    )
  })

  const [openMoveDepartmentModal] = useMoveDepartmentModal();

  const [openMoveCoursesModal] = useMoveCoursesModal();

  const [openDeleteDepartmentModal] = useModal(({ closeModal, setLoading }) => {
    function submitForm({ formatted_data, focusError }) {
      closeModal();
      if (formatted_data.remediation == "move_elsewhere") {
        openMoveCoursesModal({organization_id: organization._id, course_count: organization._course_count, membership_count: organization._membership_count, callback: () => {openConfirmDeleteDepartmentModal({})}});
      } else if (formatted_data.remediation == "archive") {
        openConfirmArchiveCoursesModal();
      } else {
        openConfirmDeleteDepartmentModal({remediation: formatted_data.remediation});
      }
    }

    return (
      <Modal title={`Delete ${organization.name}`}>
        <FormProvider onSubmit={submitForm}>
          <FormSubmitProvider>
            {({handleSubmit}) => (
              <>
                <ModalContent>
                  <RadioGroup title={`What should happen to the ${organization._course_count} course${organization._course_count != 1 ? "s" : ""} and ${organization._membership_count} educator${organization._membership_count != 1 ? "s" : ""} in this department?`} name="remediation" required="Please choose an option">
                    <RadioOption name="move_elsewhere" text="Move them to another department first."/>
                    {parent_children_count == 1 ? <RadioOption name="move_up" text="Move them up to the department above this one."/> : null}
                    <RadioOption name="archive" text="Archive the courses and remove the educators."/>
                  </RadioGroup>
                </ModalContent>
                <ModalButtons>
                  <Button text="Cancel" className="hollow" onClick={closeModal}/>
                  <Button text="Continue" className="primary" onClick={handleSubmit}/>
                </ModalButtons>
              </>
            )}
          </FormSubmitProvider>
        </FormProvider>
      </Modal>
    )
  })

  const [openConfirmArchiveCoursesModal] = useModal(({ context, closeModal, setLoading }) => {
    function confirmArchive() {
      setLoading(true);
      
      publicAction("archiveOrganizationCourses", {
        id: organization._id,
        remove_educators: true,
      }).then(function() {

        closeModal();
        navigate(".", { replace: true });
        openConfirmDeleteDepartmentModal({});

      }).catch(function(error) {
        console.error(error);
        closeModal();
        openErrorModal();
      })
    }

    return (
      <Modal title="Archive courses and remove educators?">
        <ModalContent>
          You can't undo this. <nobr>{organization._course_count} course{organization._course_count != 1 ? "s" : ""}</nobr> will be archived and <nobr>{organization._membership_count} educator{organization._membership_count != 1 ? "s" : ""}</nobr> will be removed from this department.
        </ModalContent>
        <ModalButtons>
          <Button text="Cancel" className="hollow" onClick={closeModal}/>
          <Button text="Confirm" className="destructive" onClick={confirmArchive}/>
        </ModalButtons>
      </Modal>
    )
  })

  const [openConfirmDeleteDepartmentModal] = useModal(({ context, closeModal, setLoading }) => {
    const { remediation } = context;
    function deleteDepartment() {
      setLoading(true);
      
      publicAction("deleteOrganization", {
        id: organization._id,
        ...(remediation ? { remediation } : {}),
      }).then(function({ data }) {

        if (data.success) {
          closeModal();
          navigate(".", { replace: true });
        } else {
          closeModal();
          openDeleteDepartmentModal();
        }

      }).catch(function(error) {
        console.error(error);
        closeModal();
        openErrorModal();
      })
    }

    return (
      <Modal title={`Delete ${organization.name}?`}>
        <ModalContent>
          {`You can't undo this.`}
          {remediation == "move_up" ? (
            <>
              <Spacing/>
              {`The courses and educators in this department will be moved up to the department above this one.`}
            </>
          ) : null}
        </ModalContent>
        <ModalButtons>
          <Button text="Cancel" className="hollow" onClick={closeModal}/>
          <Button text="Delete department" className="destructive" onClick={deleteDepartment}/>
        </ModalButtons>
      </Modal>
    )
  })

  const [openCantDeleteDepartmentModal] = useModal(({ closeModal }) => {
    return (
      <Modal title={`Delete inner departments first`}>
        <ModalContent>
          {`You can't delete this department because there are other departments inside it. Please relocate or delete them first.`}
        </ModalContent>
        <ModalButtons>
          <Button text="Got it" onClick={closeModal}/>
        </ModalButtons>
      </Modal>
    )
  })

  const handleOpenDeleteDepartmentModal = () => {
    if (organization.children_type == "organizations") {
      openCantDeleteDepartmentModal();
    } else if (organization._course_count > 0 || organization._membership_count > 0) {
      openDeleteDepartmentModal();
    } else {
      openConfirmDeleteDepartmentModal({});
    }
  }

  const { handleContextMenu, handleOnClick } = useMenu({
    menuItems: [
      {
        label: 'New department',
        icon: 'plus',
        onClick: () => {openNewDepartmentModal({parent_id: organization._id, parent_name: organization.name, course_count: organization._course_count, membership_count: organization._membership_count})}
      },
      "separator",
      {
        label: 'Rename',
        icon: 'rename-outline',
        onClick: () => {openRenameDepartmentModal()}
      },
      {
        label: 'Move',
        icon: 'folder-move-outline',
        onClick: () => {openMoveDepartmentModal({organization_id: organization._id})}
      },
      "separator",
      {
        label: 'Delete forever',
        icon: 'delete-forever-outline',
        onClick: () => {handleOpenDeleteDepartmentModal()}
      },
    ]
  });

  const is_mobile = useIsMobile();

  return (
    <div className="scenario_item" onClick={() => {navigate("/organizations/"+organization._id)}} onContextMenu={handleContextMenu} tabIndex={0}>
      <div className="item_icon">
        <Icon name={organization.children_type == "organizations" ? "domain" : "domain"}/>
      </div>
      <div className="item_title">{organization.name}</div>
      {/* <div className="item_date right">{organization._membership_count == 1 ? "1 educator" : (organization._membership_count > 1 ? (organization._membership_count + " educators") : "-")}</div> */}
      <div className="item_actions">
        {!is_mobile && <>
          <Button backgroundColor={"var(--header)"} icon="plus" text="New department" className="small hollow" onClick={() => {openNewDepartmentModal({parent_id: organization._id, parent_name: organization.name, course_count: organization._course_count, membership_count: organization._membership_count})}} />
          <Button backgroundColor={"var(--header)"} icon="folder-move-outline" text="Move" className="small hollow" onClick={() => {openMoveDepartmentModal({organization_id: organization._id})}} />
        </>}
      </div>
      <div className="item_more">
        <RoundIconButton icon="dots-vertical" className="small" onClick={handleOnClick} />
      </div>
    </div>
  )
}

function CourseCountItem({organization}) {
  const navigate = useNavigate();

  const [openMoveCoursesModal] = useMoveCoursesModal();

  const [openArchiveCoursesModal] = useModal(({ context, closeModal, setLoading }) => {
    function submitForm({ formatted_data, focusError }) {
      setLoading(true);
      
      publicAction("archiveOrganizationCourses", {
        id: organization._id,
        remove_educators: formatted_data.remove_educators,
      }).then(function() {

        closeModal();
        navigate(".", { replace: true });

      }).catch(function(error) {
        console.error(error);
        setLoading(false);
        focusError({name: "remove_educators", error: "Something went wrong, please try again."});
      })
    }

    return (
      <Modal title="Archive all courses?">
        <FormProvider onSubmit={submitForm}>
          <FormSubmitProvider>
            {({handleSubmit}) => (
              <>
                <ModalContent>
                  You can't undo this.
                  <Spacing/>
                  <Checkbox name="remove_educators" text="Also remove all educators from this department" />
                </ModalContent>
                <ModalButtons>
                  <Button text="Cancel" className="hollow" onClick={closeModal}/>
                  <Button text="Confirm" className="destructive" onClick={handleSubmit}/>
                </ModalButtons>
              </>
            )}
          </FormSubmitProvider>
        </FormProvider>
      </Modal>
    )
  })

  const { handleContextMenu, handleOnClick } = useMenu({
    menuItems: [
      {
        label: 'Move',
        icon: 'folder-move-outline',
        onClick: () => {openMoveCoursesModal({organization_id: organization._id, course_count: organization._course_count, membership_count: organization._membership_count})}
      },
      "separator",
      {
        label: 'Archive all courses',
        icon: 'archive',
        onClick: () => {openArchiveCoursesModal()}
      },
    ]
  });

  const is_mobile = useIsMobile();

  return (
    <div className="scenario_item" onClick={() => {navigate("/organizations/"+organization._id)}} onContextMenu={handleContextMenu} tabIndex={0}>
      <div className="item_icon">
        <Icon name="book-open-variant-outline"/>
      </div>
      <div className="item_title">{organization._course_count == 0 ? "No courses" : (organization._course_count + " course" + (organization._course_count > 1 ? "s" : ""))}, {organization._membership_count == 0 ? "no educators" : (organization._membership_count + " educator" + (organization._membership_count > 1 ? "s" : ""))}</div>
      {/* <div className="item_date right"></div> */}
      <div className="item_actions">
        {!is_mobile && <Button backgroundColor={"var(--header)"} icon="folder-move-outline" text="Move" className="small hollow" onClick={() => {openMoveCoursesModal({organization_id: organization._id, course_count: organization._course_count, membership_count: organization._membership_count})}} />}
      </div>
      <div className="item_more">
        <RoundIconButton icon="dots-vertical" className="small" onClick={handleOnClick} />
      </div>
    </div>
  )
}

function ChildGroup({children}) {
  return (
    <div className="organization_child_group">
      <div></div>
      <div style={{gridColumn: "2 / span 4"}}>{children}</div>
    </div>
  )
}

function SubCourses() {
  const { courses, _membership_count } = useLoaderData().data.value;
  const organization = useRouteLoaderData("organization").data.value;
  
  const [openNewDepartmentModal] = useNewDepartmentModal();

  return (
    <Content>
      <InlineHeader>
        <Button icon="plus" text="New department" onClick={() => {openNewDepartmentModal({parent_id: organization._id, parent_name: organization.name, course_count: courses.length, membership_count: _membership_count})}}/>
      </InlineHeader>
      <InlineSection>
        {
          courses.length > 0 ? (
            <div className="scenario_list" columns="3">
              <div className="scenario_list_header">
                <div></div>
                <div>Course name</div>
                <div>Learners</div>
                <div>Educators</div>
                <div>Date created</div>
                <div></div>
              </div>
              {courses.sort((a, b) => b.date_created - a.date_created).map((course) => <CourseItem key={course._id} course={course}/>)}
            </div>
          ) : <GenericEmptyListPlaceholder text="There aren't any departments or courses yet"/>
        }
      </InlineSection>
    </Content>
  )
}

function CourseItem({course}) {
  const navigate = useNavigate();

  const { openArchiveCourseModal } = useArchiveCourseModal({ course_id: course._id });

  const { handleContextMenu, handleOnClick } = useMenu({
    menuItems: [
      {
        label: 'Archive',
        icon: 'archive',
        onClick: () => {openArchiveCourseModal()}
      },
    ]
  });

  return (
    <div className="scenario_item" onClick={() => {navigate("/organizations/"+course.organization_id+"/courses/"+course._id)}} onContextMenu={handleContextMenu} tabIndex={0}>
      <div className="item_icon">
        <Icon name="book-open-variant-outline"/>
      </div>
      <div className="item_title">{course.name}</div>
      <div className="item_date">{course._members.filter(a => a.role == "student").length > 0 ? (course._members.filter(a => a.role == "student").length + " learner" + (course._members.filter(a => a.role == "student").length > 1 ? "s" : "")) : "-"}</div>
      <div className="item_date">{course._members.filter(a => a.role == "educator").length > 0 ? (course._members.filter(a => a.role == "educator").sort((a, b) => a.date_joined - b.date_joined)[0].user.name + (course._members.filter(a => a.role == "educator").length > 1 ? (" +" + (course._members.filter(a => a.role == "educator").length - 1)) : "")) : "-"}</div>
      <div className="item_date">{(new Intl.DateTimeFormat('en-US', { year: 'numeric', month: 'short', day: 'numeric' }).format(course.date_created))}</div>
      <div className="item_more">
        <RoundIconButton icon="dots-vertical" className="small" onClick={handleOnClick} />
      </div>
    </div>
  )
}

export function OrgPeople() {
  const navigate = useNavigate();
  const people = useLoaderData().data.value;
  const organization = useRouteLoaderData("organization").data.value;

  const admins = people.members
    .filter(a => a.roles.includes("admin"))
    .sort((a, b) => {
      if (!a.approved && b.approved) return -1; // Pending requests first
      if (a.approved && !b.approved) return 1;
      if (a._id.charAt(0) == "_" && b._id.charAt(0) != "_") return -1; // Pending invites next
      if (a._id.charAt(0) != "_" && b._id.charAt(0) == "_") return 1;
      return (a.name || a.email).localeCompare(b.name || b.email); // Then sort by name or email within that
    });
  const educators = people.members
    .filter(a => a.roles.includes("educator") && !a.roles.includes("admin"))
    .sort((a, b) => {
      if (!a.approved && b.approved) return -1;
      if (a.approved && !b.approved) return 1;
      if (a._id.charAt(0) == "_" && b._id.charAt(0) != "_") return -1;
      if (a._id.charAt(0) != "_" && b._id.charAt(0) == "_") return 1;
      return (a.name || a.email).localeCompare(b.name || b.email);
    });

  const [openAddEducatorModal] = useModal(({ context, closeModal, setLoading }) => {
    function submitForm({ formatted_data, focusError }) {
      setLoading(true);

      publicAction("addOrganizationMembers", {
        organization_id: organization._id,
        people: formatted_data.people,
        roles: [context.role]
      }).then(function() {

        closeModal();
        navigate(".", { replace: true });

      }).catch(function(error) {
        console.error(error);
        setLoading(false);
        focusError({name: "people", error: "Something went wrong, please try again."});
      })
    }

    return (
      <Modal title={`Add ${context.role == "educator" ? "educators" : "managers"}`}>
        <FormProvider onSubmit={submitForm}>
          <FormSubmitProvider>
            {({handleSubmit, has_changed}) => (
              <>
                <ModalContent>
                  <ShareBox name="people"/>
                  <Spacing/>
                  {"We'll notify them that they've been added."}
                </ModalContent>
                <ModalButtons>
                  <Button text="Cancel" className="hollow" onClick={closeModal}/>
                  <Button text={`Add ${context.role == "educator" ? "educators" : "managers"}`} className="primary" disabled={!has_changed} onClick={handleSubmit}/>
                </ModalButtons>
              </>
            )}
          </FormSubmitProvider>
        </FormProvider>
      </Modal>
    )
  })
  
  return <Content>
    <div className="small_width_section">
      <InlineHeader title={!organization.parent ? "Organization Managers" : "Department Managers"}>
        <Button icon="account-plus" text="Add managers" onClick={() => {openAddEducatorModal({role: "admin"})}}/>
      </InlineHeader>
      <InlineSection>
        {admins.length > 0 ? (
          admins.map((person) => <PersonItem key={person._id} person={person}/>)
        ) : <GenericEmptyListPlaceholder text="No managers"/>}
      </InlineSection>
      {organization.children_type == "courses" ? <>
        <InlineHeader title="Educators">
          <Button icon="account-plus" text="Add educators" onClick={() => {openAddEducatorModal({role: "educator"})}}/>
        </InlineHeader>
        <InlineSection>
          {educators.length > 0 ? (
            educators.map((person) => <PersonItem key={person._id} person={person}/>)
          ) : <GenericEmptyListPlaceholder text="No educators"/>}
        </InlineSection>
      </> : null}
    </div>
  </Content>
}

function PersonItem({person}) {
  const navigate = useNavigate();
  const organization = useRouteLoaderData("organization").data.value;
  const [openErrorModal] = useErrorModal();

  const [openRemovePersonModal] = useModal(({ closeModal, setLoading }) => {
    function submitForm({ formatted_data, focusError }) {
      setLoading(true);

      publicAction("removeOrganizationMember", {
        organization_id: organization._id,
        id: person._id,
        remove_from_courses: person.approved && formatted_data.remove_from_courses
      }).then(function() {
        closeModal();
        navigate(".", { replace: true });
      }).catch(function(error) {
        console.error(error);
        closeModal();
        openErrorModal();
      })

    }

    return (
      <Modal title={person.approved ? "Remove this person?" : "Deny join request?"}>
        <FormProvider onSubmit={submitForm}>
          <FormSubmitProvider>
            {({handleSubmit}) => (
              <>
                <ModalContent>
                  {person.approved ? "They won't be notified that they were removed." : "They will be notified that their request was denied."}
                  {person.approved ? <>
                    <Spacing/>
                    <Checkbox name="remove_from_courses" text="Also remove them from any courses they teach in this department" default_value={true}/>
                  </> : null}
                </ModalContent>
                <ModalButtons>
                  <Button text="Cancel" className="hollow" onClick={closeModal}/>
                  <Button text={person.approved ? "Remove" : "Deny"} className={person.approved ? "destructive" : "primary"} onClick={handleSubmit}/>
                </ModalButtons>
              </>
            )}
          </FormSubmitProvider>
        </FormProvider>
      </Modal>
    )
  })

  const [openApprovePersonModal] = useModal(({ closeModal, setLoading }) => {
    function handleSubmit() {
      setLoading(true);

      publicAction("approveOrganizationMember", {
        organization_id: organization._id,
        id: person._id
      }).then(function() {
        closeModal();
        navigate(".", { replace: true });
      }).catch(function(error) {
        console.error(error);
        closeModal();
        openErrorModal();
      })
    }

    return (
      <Modal title="Approve join request?">
        <ModalContent>
          {`${person.name} will join as ${person.roles.includes("admin") ? "a manager" : "an educator"}. They will be notified that their request was approved.`}
        </ModalContent>
        <ModalButtons>
          <Button text="Cancel" className="hollow" onClick={closeModal}/>
          <Button text="Approve" className="primary" onClick={handleSubmit}/>
        </ModalButtons>
      </Modal>
    )
  })

  const { handleContextMenu, handleOnClick } = useMenu({
    menuItems: [
      ...(!person.approved ? [{
        label: "Approve",
        icon: "check",
        onClick: () => {openApprovePersonModal()}
      }] : []),
      {
        label: person.approved ? "Remove" : "Deny",
        icon: "close",
        onClick: () => {openRemovePersonModal()}
      }
    ]
  });

  const small_buttons = useBreakpoint((width, height) => width <= 600);

  return (
    <div className="person_item" onContextMenu={handleContextMenu}>
      <div className="person_item_icon">
        <Icon name={person._id.charAt(0) != "_" ? "account-circle-outline" : "email-outline"}/>
      </div>
      <div className="person_item_text">
        {person._id.charAt(0) != "_" ? <div>{person.name || "(No name)"}</div> : null}
        <div>{person.email}</div>
        {person._id.charAt(0) == "_" ? <div>Pending invitation</div> : null}
      </div>
      {!person.approved ? <div className="person_item_buttons">
        <Button icon="check" text={!small_buttons && "Approve"} className="small hollow green_text" onClick={() => {openApprovePersonModal()}}/>
        <Button icon="close" text={!small_buttons && "Deny"} className="small hollow red_text" onClick={() => {openRemovePersonModal()}}/>
      </div> : null}
      <div className="person_item_more">
        <RoundIconButton icon="dots-vertical" className="small" onClick={handleOnClick} />
      </div>
    </div>
  )
}

function useAddDomainModal() {
  const navigate = useNavigate();
  const { organization_data: organization, domains } = useRouteLoaderData("orgsettings").data.value;
  const [openErrorModal] = useErrorModal();
  const { user } = useUser();
  const is_super_admin = useIsSuperAdmin();

  const [openAddDomainModal] = useModal(({ context, closeModal, setLoading }) => {
    function submitForm({ formatted_data, focusError }) {

      const domainRegex = /^([a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}$/;
      if (!domainRegex.test(formatted_data.domain)) {
        focusError({name: "domain", error: "Please enter a valid domain name. Don't include http:// or a trailing slash. Don't include wildcards (*)."});
        return;
      }

      if (domains.filter(d => d._id != context?.domain?._id).some(d => d.domain.toLowerCase() === formatted_data.domain.toLowerCase())) {
        focusError({name: "domain", error: "Domain already exists"});
        return;
      }

      setLoading(true);

      let domain = formatted_data.domain.toLowerCase();
      
      publicAction(context?.domain ? "updateDomain" : "createDomain", {
        id: context?.domain?._id,
        organization_id: organization._id,
        domain: domain,
        provider: formatted_data.provider,
        ...(context?.domain?.verified ? { remediation: formatted_data.remediation } : {}),
      }).then(function() {

        let user_email_domain = user.email.split("@")[1];

        if (!context?.domain && (domain == user_email_domain || domain.endsWith("."+user_email_domain) || is_super_admin)) {

          publicAction("verifyDomain", {
            id: context?.domain?._id,
            organization_id: organization._id,
            domain: domain,
          }).then(function() {

            closeModal();
            navigate(".", { replace: true });

          }).catch(function(error) {
            console.error(error);
            closeModal();
            openErrorModal();
            navigate(".", { replace: true });
          })

        } else {

          closeModal();
          navigate(".", { replace: true });

        }

      }).catch(function(error) {
        console.error(error);
        setLoading(false);
        focusError({name: "domain", error: "Something went wrong."});
      })
    }

    const initial_data = context?.domain ? {
      domain: context.domain.domain,
      provider: context.domain.provider,
    } : undefined;

    return (
      <Modal title={context?.domain ? "Edit sign-in email domain" : "Add sign-in email domain"}>
        <FormProvider onSubmit={submitForm} initial_data={initial_data}>
          <FormSubmitProvider>
            {({handleSubmit}) => (
              <>
                <ModalContent>
                  <Textbox name="domain" title="Domain" placeholder="student.example.com" required={true} readOnly={context?.domain} />
                  <Spacing/>
                  <Dropdown name="provider" title="Sign-in provider" required={true}>
                    <DropdownOption name="password" text="Password"/>
                    <DropdownOption name="microsoft.com" text="Microsoft / Azure AD / Entra ID"/>
                    <DropdownOption name="google.com" text="Google"/>
                  </Dropdown>
                  {context?.domain?.verified ? (
                    <>
                      <Spacing/>
                      <RadioGroup title="What should happen to existing user accounts?" name="remediation" required="Please choose an option">
                        <RadioOption name="keep" text="Existing users keep their existing sign-in provider. Only new accounts are affected from now on."/>
                        <RadioOption name="switch" text="Switch all existing user accounts to the new sign-in provider."/>
                      </RadioGroup>
                    </>
                  ) : null}
                </ModalContent>
                <ModalButtons>
                  <Button text="Cancel" className="hollow" onClick={closeModal}/>
                  <Button text="Submit" className="primary" onClick={handleSubmit}/>
                </ModalButtons>
              </>
            )}
          </FormSubmitProvider>
        </FormProvider>
      </Modal>
    )
  })

  return [openAddDomainModal];
}

export function OrgSettings() {
  const navigate = useNavigate();
  const { organization_data: organization, domains } = useLoaderData().data.value;
  const initial_data = organization;
  const [submitting, setIsSubmitting] = useState(false);
  const is_reloading = useIsReloading();
  const [openErrorModal] = useErrorModal();

  function submitForm({ formatted_data }) {
    setIsSubmitting(true);

    if (organization.parent) {
      formatted_data.shortname = formatted_data.name;
    }

    publicAction("updateOrganization", {
      id: organization._id,
      data: formatted_data
    }).then(function() {
      setIsSubmitting(false);
      navigate(".", { replace: true });
    }).catch(function(error) {
      console.error(error);
      setIsSubmitting(false);
      openErrorModal();
    })
  }

  const [openAddDomainModal] = useAddDomainModal();

  return (
    <FormProvider onSubmit={submitForm} initial_data={initial_data} unsaved_modal="organization_settings">
      <Content>
        <div className="small_width_section">
          <InlineHeader title="Basic details"/>
          <InlineSection>
            <Textbox name="name" title={!organization.parent ? "Organization name" : "Department name"} autofocus={true} required={true}/>
            {
              !organization.parent ? (
                <>
                  <Spacing/>
                  <Textbox name="shortname" title="Short name" required={true}/>
                </>
              ) : null
            }
            <HalfSpacing/>
          </InlineSection>
          {organization.children_type == "courses" ? <>
            <InlineHeader title="Access"/>
            <InlineSection>
              <Checkbox name="join_requests" text="Allow anyone in your organization to request to join this department as an educator"/>
              <Checkbox name="add_others" text="Allow educators in this department to invite anyone else in your organization to join this department as an educator"/>
            </InlineSection>
          </> : null}
          {domains.length > 0 || !organization.parent ? (
            <>
              <InlineHeader title="Sign-in email domains">
                <Button icon="plus" text="Add domain" onClick={() => {openAddDomainModal()}}/>
              </InlineHeader>
              <InlineSection>
                {domains.length > 0 ? (
                  <div className="scenario_list" columns="2">
                    <div className="scenario_list_header">
                      <div></div>
                      <div>Domain</div>
                      <div>Provider</div>
                      <div>Status</div>
                      <div></div>
                    </div>
                    {domains.map((domain) => <DomainItem key={domain._id} domain={domain}/>)}
                  </div>
                ) : <GenericEmptyListPlaceholder text="No sign-in email domains"/>}
              </InlineSection>
            </>
          ) : null}
        </div>
      </Content>
      <Pagination getContainerElement={() => document.querySelector("#organization_settings")}>
        <FormSubmitProvider>
          {({handleSubmit, has_changed}) => {
            return <Button className="primary" text="Save changes" onClick={handleSubmit} disabled={submitting || !has_changed || is_reloading} loading={submitting}/>
          }}
        </FormSubmitProvider>
      </Pagination>
    </FormProvider>
  )
}

function DomainItem({domain}) {
  const navigate = useNavigate();
  const [openAddDomainModal] = useAddDomainModal();
  const is_super_admin = useIsSuperAdmin();

  const [openErrorModal] = useErrorModal();

  const [openConfirmDeleteDomainModalVerified] = useModal(({ closeModal }) => {
    function submitForm({ formatted_data }) {
      closeModal();
      if (formatted_data.org_remediation == "yes") {
        openConfirmDeleteDomainModalSuggestDisabling();
      } else {
        openConfirmDeleteDomainModalRemediation();
      }
    }

    if (domain.date > Date.now() - 30 * 24 * 60 * 60 * 1000 && !is_super_admin) {
      return (
        <Modal title="Delete sign-in email domain">
          <ModalContent>
            For security reasons, you need to contact support to delete this domain. Email <a href="mailto:support@sapientclinician.com" className="links">support@sapientclinician.com</a> to request deletion.
          </ModalContent>
          <ModalButtons>
            <Button text="Got it" onClick={closeModal}/>
          </ModalButtons>
        </Modal>
      )
    }

    return (
      <Modal title="Delete sign-in email domain">
        <FormProvider onSubmit={submitForm}>
          <FormSubmitProvider>
            {({handleSubmit}) => (
              <>
                <ModalContent>
                  <RadioGroup title="Should existing users stay in the organization?" name="org_remediation" required="Please choose an option">
                    <RadioOption name="yes" text="Yes, users stay in the organization"/>
                    <RadioOption name="no" text="No, users are removed from the organization"/>
                  </RadioGroup>
                </ModalContent>
                <ModalButtons>
                  <Button text="Cancel" className="hollow" onClick={closeModal}/>
                  <Button text="Next step" className="primary" onClick={handleSubmit}/>
                </ModalButtons>
              </>
            )}
          </FormSubmitProvider>
        </FormProvider>
      </Modal>
    )
  })

  const [openConfirmDeleteDomainModalSuggestDisabling] = useModal(({ closeModal }) => {
    return (
      <Modal title="You should disable this domain instead">
        <ModalContent>
          If you want existing users to stay in the organization, you should disable this domain instead of deleting it. Disabling the domain will prevent new users from creating accounts, but existing users will not be affected.
        </ModalContent>
        <ModalButtons>
          <Button text="Got it" onClick={closeModal}/>
        </ModalButtons>
      </Modal>
    )
  })

  const [openConfirmDeleteDomainModalRemediation] = useModal(({ closeModal, setLoading }) => {
    function submitForm({ formatted_data }) {
      setLoading(true);
      
      publicAction("deleteDomain", {
        id: domain._id,
        domain: domain.domain,
        organization_id: domain.organization_id,
        remediation: formatted_data?.remediation,
      }).then(function() {

        closeModal();
        navigate(".", { replace: true });

      }).catch(function(error) {
        console.error(error);
        closeModal();
        openErrorModal();
      })

    }

    return (
      <Modal title="Delete sign-in email domain">
        <FormProvider onSubmit={submitForm}>
          <FormSubmitProvider>
            {({handleSubmit}) => (
              <>
                <ModalContent>
                  <RadioGroup title="All existing users will be removed from the organization. What should happen to their accounts?" name="remediation" required="Please choose an option">
                    <RadioOption name="reset" text="Accounts are preserved. Sign-in provider is switched to password. Users can still sign in after verifying their email and setting a password. After signing in, users can change their email address to a personal email if they want. Users can still view their content."/>
                    <RadioOption name="delete" text="Accounts are permanently deleted."/>
                  </RadioGroup>
                </ModalContent>
                <ModalButtons>
                  <Button text="Cancel" className="hollow" onClick={closeModal}/>
                  <Button text="Delete domain" className="destructive" onClick={handleSubmit}/>
                </ModalButtons>
              </>
            )}
          </FormSubmitProvider>
        </FormProvider>
      </Modal>
    )
  })

  const [openConfirmDeleteDomainModalUnverified] = useModal(({ closeModal, setLoading }) => {
    function submitForm() {
      setLoading(true);
      
      publicAction("deleteDomain", {
        id: domain._id,
        domain: domain.domain,
        organization_id: domain.organization_id
      }).then(function() {

        closeModal();
        navigate(".", { replace: true });

      }).catch(function(error) {
        console.error(error);
        closeModal();
        openErrorModal();
      })

    }

    return (
      <Modal title="Delete sign-in email domain">
        <ModalContent>
          This domain isn't verified yet, so this action is without consequence.
        </ModalContent>
        <ModalButtons>
          <Button text="Cancel" className="hollow" onClick={closeModal}/>
          <Button text="Confirm" className="primary" onClick={() => {submitForm()}}/>
        </ModalButtons>
      </Modal>
    )
  })

  const openConfirmDeleteDomainModal = domain.verified ? openConfirmDeleteDomainModalVerified : openConfirmDeleteDomainModalUnverified;

  const [openTransferDomainModal] = useModal(({ closeModal }) => {
    return (
      <Modal title="Transfer sign-in email domain">
        <ModalContent>
          Please contact support for assistance with transferring this domain to another organization. Email <a href="mailto:support@sapientclinician.com" className="links">support@sapientclinician.com</a> to request a transfer.
        </ModalContent>
        <ModalButtons>
          <Button text="Got it" onClick={closeModal}/>
        </ModalButtons>
      </Modal>
    )
  })

  const [openDisableDomainModal] = useModal(({ closeModal, setLoading }) => {
    function confirmDisable() {
      setLoading(true);
      
      publicAction("disableDomain", {
        id: domain._id,
        organization_id: domain.organization_id
      }).then(function() {

        closeModal();
        navigate(".", { replace: true });

      }).catch(function(error) {
        console.error(error);
        closeModal();
        openErrorModal();
      })

    }

    return (
      <Modal title="Disable sign-ups?">
        <ModalContent>
          New users will no longer be able to create new accounts with this domain. Existing users will not be affected.
        </ModalContent>
        <ModalButtons>
          <Button text="Cancel" className="hollow" onClick={closeModal}/>
          <Button text="Confirm" className="primary" onClick={confirmDisable}/>
        </ModalButtons>
      </Modal>
    )
  })

  const [openEnableDomainModal] = useModal(({ closeModal, setLoading }) => {
    function confirmEnable() {
      setLoading(true);
      
      publicAction("enableDomain", {
        id: domain._id,
        organization_id: domain.organization_id
      }).then(function() {

        closeModal();
        navigate(".", { replace: true });

      }).catch(function(error) {
        console.error(error);
        closeModal();
        openErrorModal();
      })

    }

    return (
      <Modal title="Re-enable sign-ups?">
        <ModalContent>
          New users will once again be able to create new accounts with this domain.
        </ModalContent>
        <ModalButtons>
          <Button text="Cancel" className="hollow" onClick={closeModal}/>
          <Button text="Confirm" className="primary" onClick={confirmEnable}/>
        </ModalButtons>
      </Modal>
    )
  })

  const openEnableOrDisableDomainModal = domain.disabled ? openEnableDomainModal : openDisableDomainModal;

  const { handleContextMenu, handleOnClick } = useMenu({
    menuItems: [
      {
        label: "Edit",
        icon: "pencil-outline",
        onClick: () => {openAddDomainModal({domain})}
      },
      "separator",
      {
        label: domain.disabled ? "Enable sign-ups" : "Disable sign-ups",
        icon: domain.disabled ? "play" : "stop",
        disabled: !domain.verified,
        onClick: () => {openEnableOrDisableDomainModal()}
      },
      {
        label: 'Transfer',
        icon: 'folder-move-outline',
        disabled: !domain.verified,
        onClick: () => {openTransferDomainModal()}
      },
      "separator",
      {
        label: 'Delete',
        icon: 'delete-forever-outline',
        onClick: () => {openConfirmDeleteDomainModal()}
      }
    ]
  });

  return (
    <div className="scenario_item" onClick={() => {openAddDomainModal({domain})}} onContextMenu={handleContextMenu} tabIndex={0}>
      <div className="item_icon">
        <Icon name="at"/>
      </div>
      <div className="item_title">{domain.domain}</div>
      <div className="item_date">{{"password": "Password", "microsoft.com": "Microsoft", "google.com": "Google"}[domain.provider]}</div>
      {domain.verified ? (domain.disabled ? <div className="item_date">Sign-ups disabled</div> : <div className="item_date" style={{color: "var(--greentext)"}}>Active</div>) : <div className="item_date" style={{color: "var(--redtext)"}}>Awaiting support team verification</div>}
      <div className="item_more">
        <RoundIconButton icon="dots-vertical" className="small" onClick={handleOnClick} />
      </div>
    </div>
  )
}